import * as React from 'react'
import { ReactElement } from 'react'
import { Button, Text } from '@fluentui/react-components'
import { ToastContentProps } from 'react-toastify'
import useEmailFilingHandler, { EmailFilingParams } from '../../hooks/email/useEmailFilingHandler'
import {
    ArrowRepeatAllFilled,
    ArrowRepeatAllRegular,
    bundleIcon,
    DocumentDismissFilled,
    DocumentDismissRegular,
} from '@fluentui/react-icons'
import ErrorPopover from './ErrorPopover'
import { QueueData, QueueStatus } from '../../hooks/useQueueStateHandler'
import { useDocumentToastStyles } from './DocumentToastContent'
import { useTranslation } from 'react-i18next'

const RepeatIcon = bundleIcon(ArrowRepeatAllFilled, ArrowRepeatAllRegular)
const DismissIcon = bundleIcon(DocumentDismissFilled, DocumentDismissRegular)

function getEmailFilingToastMessage(
    { status, context }: QueueData<EmailFilingParams, EmailFilingParams, EmailFilingParams>,
    translate: (key: string, options?: any) => string,
) {
    const referencesString = context.data.references.join(', ')

    return new Map<QueueStatus, ReactElement | string>([
        [QueueStatus.Dispatch, translate('toast.email.dispatch')],
        [
            QueueStatus.Queued,
            translate('toast.email.queued', {
                count: context.data.references.length,
                referencesString: referencesString,
            }),
        ],
        [QueueStatus.Delayed, translate('toast.email.delayed')],
        [QueueStatus.Cancelled, translate('toast.email.cancelled')],
        [
            QueueStatus.Finished,
            translate('toast.email.finished', {
                count: context.data.references.length,
                referencesString: referencesString,
            }),
        ],
        [QueueStatus.Failed, translate('toast.email.failed')],
        [
            QueueStatus.Duplicate,
            translate('toast.email.duplicate', {
                count: context.data.references.length,
                referencesString: referencesString,
            }),
        ],
    ]).get(status.current)
}

export default function EmailFilingToastContent(
    props: ToastContentProps<QueueData<EmailFilingParams, EmailFilingParams, EmailFilingParams>>,
): ReactElement {
    const { t: translate } = useTranslation()

    const styles = useDocumentToastStyles()
    const handler = useEmailFilingHandler()
    const status = props.data.status.current

    return (
        <div className={styles.toaster}>
            <Text>{getEmailFilingToastMessage(props.data, translate)}</Text>
            {status === QueueStatus.Duplicate && (
                <div className={styles.button}>
                    <Button
                        icon={<RepeatIcon />}
                        appearance="subtle"
                        onClick={() => {
                            handler.dispatch(props.toastProps.toastId as string, props.data.context, true)
                        }}
                    >
                        {translate('toast.restart')}
                    </Button>
                </div>
            )}
            {![QueueStatus.Cancelled, QueueStatus.Duplicate, QueueStatus.Failed, QueueStatus.Finished].includes(
                status,
            ) && (
                <div className={styles.button}>
                    <Button
                        icon={<DismissIcon />}
                        appearance="subtle"
                        onClick={() => {
                            handler.transition(props.toastProps.toastId as string, QueueStatus.Cancelled)
                        }}
                    >
                        {translate('toast.cancel')}
                    </Button>
                </div>
            )}
            <ErrorPopover error={props.data.status.error} styles={styles} />
        </div>
    )
}
